import React, { ReactNode, useContext, useEffect, useRef, useState } from "react";
import { Grid, Box } from "@material-ui/core";
import { Form, Formik, FormikProps } from "formik";
import FormGroup from "@mui/material/FormGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import { AxiosResponse } from "axios";
import useEnv from "../../../../hooks/env";
import CustomDialog from "../../../../components/Dialog/CustomDialog";
import ConfirmationDialogContext from "../../../../components/Dialog/ConfirmationDialog/ConfirmationDialogContext";
import ErrorSnackbarContext from "../../../../contexts/ErrorSnackbarContext";
import InfoBox from "../../../../components/InfoBox";
import CustomDropdownMultiple from "../../../../components/Input/CustomDropdownMultiple";
import CustomButton from "../../../../components/Button/CustomButton";
import MatrixExportConfigurationApi from "../../../../api/services/MatrixExportConfigurationApi";
import CustomLoading from "../../../../components/Alert/CustomLoading";
import { tesaDarkRedError } from "../../../../styles/colors";
import matrixConfigurationOptionsMap from "../TabbedDialog/TestCasesTab/utils/MatrixConfigurationOptionsMap";
import matrixConfigurationOptionsReverseMap from "../TabbedDialog/TestCasesTab/utils/MatrixConfigurationOptionsReverseMap";
import {
  IGetOption,
  IMatrixExportConfigurationDialog,
  IOptionalParametersFormFriendly,
  IPrepareValuesForApi,
  MappedMatrixConfiguration,
  MatrixConfigurationFormFields,
} from "./Interfaces";
import { useStyles } from "./MatrixExportConfigurationStyle";
import {
  MatrixExportConfigurationParameterRequest,
  MatrixExportConfigurationParameterValue,
  MatrixExportConfigurationResponse,
} from "../../../../api/models/test-series/TestSeriesMatrixConfiguration";

const getInfoText = (option: string) => {
  return `Select the ${option} to be displayed in the export matrix. 
    The order in which the ${option} are shown in the input field illustrates the order in the test result matrix.`;
};

const availableOptions = (selectedRowOptions: string[], selectedColumnOptions: string[], options: string[]) => {
  return {
    availableRowOptions: options.filter((option) => !selectedColumnOptions.includes(option)),
    availableColumnOptions: options.filter((option) => !selectedRowOptions.includes(option)),
  };
};

const getOptions = (response: IGetOption[]) => {
  return response.map((option) => {
    return option.matrixExportConfigurationParameterType;
  });
};

const getOptionalParametersFormFriendly = (response: IOptionalParametersFormFriendly) => {
  return response?.parameters?.map((parameter: MatrixExportConfigurationParameterValue) => {
    return parameter?.referenceName;
  });
};

const getOptionsParameters = (response: IGetOption[]) => {
  return response
    ?.map((option: IGetOption) => {
      if (option.matrixExportConfigurationParameterValues.length > 1) {
        return {
          name: option.matrixExportConfigurationParameterType,
          parameters: option.matrixExportConfigurationParameterValues,
        };
      }
      return {
        name: option.matrixExportConfigurationParameterType,
        parameters: [],
      };
    })
    .filter((option) => option.parameters.length > 0);
};

const getInitialValues = (values: {
  [x: string]: unknown;
  rows: string[];
  columns: string[];
  options?: string[];
  raw?: string[];
  isActive?: boolean;
  optionParameters?: Array<{ name: string; [key: string]: unknown }>;
}) => {
  const optionParametersObject: { [key: string]: unknown } = {};
  values.optionParameters?.forEach((parameters) => {
    const { name } = parameters;
    optionParametersObject[name] = getOptionalParametersFormFriendly(parameters);
  });

  return {
    rows: values.rows,
    columns: values.columns,
    options: values.options || [],
    raw: values.raw,
    isActive: values.isActive,
    ...optionParametersObject,
  };
};

const prepareValuesForApi = (rawValue: IPrepareValuesForApi) => {
  const { formValues, rawResponse: references, prepare } = rawValue;
  return references
    .filter((reference) => prepare.includes(reference.matrixExportConfigurationParameterType))
    .map((reference) => {
      if (reference.matrixExportConfigurationParameterValues.length > 1) {
        const filteredValues = reference.matrixExportConfigurationParameterValues.filter((value) =>
          (formValues[reference.matrixExportConfigurationParameterType] as string[]).includes(value.referenceName),
        );

        return {
          ...reference,
          matrixExportConfigurationParameterValues: filteredValues,
        };
      }
      return reference;
    });
};

const handleHttpResponseAndPrepareData = (response: AxiosResponse<MatrixExportConfigurationResponse>) => {
  const {
    data: { columns: responseColumns, rows: responseRows, active },
  } = response;

  const rows = getOptions(responseRows as unknown as IGetOption[]);
  const columns = getOptions(responseColumns as unknown as IGetOption[]);

  const rowParameters = getOptionsParameters(responseRows as unknown as IGetOption[]);
  const columnParameters = getOptionsParameters(responseColumns as unknown as IGetOption[]);

  const options = [...columns, ...rows];
  const raw = [...responseColumns, ...responseRows];
  const optionParameters = [...columnParameters, ...rowParameters];

  return {
    columns,
    rows,
    active,
    options,
    optionParameters,
    raw,
  };
};

const reverseMatrixConfig = (values: MatrixConfigurationFormFields): MatrixConfigurationFormFields => {
  return {
    options: [],
    columns: values.columns.map((column) => matrixConfigurationOptionsReverseMap.get(column)!),
    rows: values.rows.map((row) => matrixConfigurationOptionsReverseMap.get(row)!),
  };
};

const matrixConfigView = (data: MappedMatrixConfiguration) => {
  const columns = data.columns.map((column) => matrixConfigurationOptionsMap.get(column)!);
  const rows = data.rows.map((row) => matrixConfigurationOptionsMap.get(row)!);
  const options = data.options.map((option) => matrixConfigurationOptionsMap.get(option)!);
  return {
    columns,
    rows,
    options,
  };
};

function MatrixExportConfigurationDialog({
  open,
  testRequestId,
  testSeriesId,
  testVersion,
  handleClose,
}: IMatrixExportConfigurationDialog) {
  const { api } = useEnv();
  const matrixExportConfigurationApi = new MatrixExportConfigurationApi(api);
  const classes = useStyles();

  const formRef = useRef<FormikProps<MatrixConfigurationFormFields>>(null);

  const { showConfirmationDialog } = useContext(ConfirmationDialogContext);
  const { handleHttpError, showSnackbar } = useContext(ErrorSnackbarContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [matrixExportConfiguration, setMatrixExportConfiguration] = useState<MatrixConfigurationFormFields>({
    columns: [],
    rows: [],
    options: [],
    raw: [],
    isActive: false,
  });

  const initialValues = getInitialValues(matrixExportConfiguration);

  const handleOnSubmit = (values: MatrixConfigurationFormFields) => {
    const { raw: rawResponse } = matrixExportConfiguration;
    const { columns: reverseMatrixColumns, rows: reverseMatrixRows } = reverseMatrixConfig(values);

    const columns = prepareValuesForApi({
      formValues: values,
      rawResponse: rawResponse as MatrixExportConfigurationParameterRequest[],
      prepare: reverseMatrixColumns,
    });

    const rows = prepareValuesForApi({
      formValues: values,
      rawResponse: rawResponse as MatrixExportConfigurationParameterRequest[],
      prepare: reverseMatrixRows,
    });

    if (testRequestId && testSeriesId === 0) {
      const active: boolean = !!values.isActive;
      matrixExportConfigurationApi
        .updateMatrixExportConfiguration(testRequestId, { testRequestId, columns, rows, active })
        .then(() => showSnackbar("Test Request Export updated", "success"))
        .catch(handleHttpError)
        .finally(handleClose);
    }

    if (testRequestId && testSeriesId !== 0) {
      const active: boolean = true;
      matrixExportConfigurationApi
        .updateTestSeriesMatrixExportConfiguration(testSeriesId, testVersion, {
          testSeriesId,
          version: testVersion,
          columns,
          rows,
          active,
        })
        .then(() => showSnackbar("Test Series Export updated", "success"))
        .catch(handleHttpError)
        .finally(handleClose);
    }
  };

  useEffect(() => {
    if (testRequestId && testSeriesId === 0) {
      matrixExportConfigurationApi
        .getMatrixConfiguration(testRequestId)
        .then(handleHttpResponseAndPrepareData)
        .then((response) => {
          const { optionParameters, raw, active: isActive } = response;
          const { columns, rows, options } = matrixConfigView(response);

          setMatrixExportConfiguration({
            columns,
            rows,
            options,
            optionParameters,
            raw,
            isActive,
          });
        })
        .catch(handleHttpError)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [testRequestId, testSeriesId]);

  useEffect(() => {
    if (testRequestId && testSeriesId !== 0) {
      matrixExportConfigurationApi
        .getTestSeriesMatrixExportConfiguration(testSeriesId, testVersion)
        .then(handleHttpResponseAndPrepareData)
        .then((response) => {
          const { optionParameters, raw } = response;
          const { columns, rows, options } = matrixConfigView(response);
          setMatrixExportConfiguration({
            columns,
            rows,
            options,
            optionParameters,
            raw,
          });
        })
        .catch(handleHttpError)
        .finally(() => {
          setLoading(false);
        });
    }
  }, [testSeriesId, testVersion]);

  if (loading || !matrixExportConfiguration) {
    return <CustomLoading />;
  }

  const showMatrixConfigurationConfirmationDialog = () => {
    if (formRef && formRef.current && formRef.current.dirty) {
      showConfirmationDialog({
        headline: "You have unsaved changes",
        text: "If you continue, your current changes to the matrix configuration will be discarded.",
        confirmText: "Save",
        additionalAction: {
          id: "DISCARD",
          text: "Discard",
          textColor: tesaDarkRedError,
        },
      }).then((choice) => {
        if (choice === "DEFAULT") {
          formRef?.current?.handleSubmit();
        } else {
          formRef?.current?.handleReset();
        }
        handleClose();
      });
    } else {
      handleClose();
    }
  };

  return (
    <CustomDialog fullWidth onClose={showMatrixConfigurationConfirmationDialog} open={open}>
      <Box className={classes.container}>
        <h2 className={classes.header}>Export Matrix Configuration</h2>
      </Box>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={{
          ...initialValues,
          options: initialValues.options || [],
        }}
        onSubmit={(values: MatrixConfigurationFormFields) => handleOnSubmit(values)}
      >
        {(formikProps: FormikProps<MatrixConfigurationFormFields>): ReactNode => {
          const { dirty, handleChange, isSubmitting, values } = formikProps;
          const { availableRowOptions, availableColumnOptions } = availableOptions(
            values.rows,
            values.columns,
            matrixExportConfiguration.options,
          );
          const formFieldKeys = Object.keys(values);
          return (
            <Form>
              <Grid container direction="column" spacing={1}>
                <Grid item className={classes.dropdownItems}>
                  {testSeriesId === 0 && (
                    <InfoBox text={getInfoText("Override Test Series Export Configuration")}>
                      <FormGroup>
                        <FormControlLabel
                          control={<Checkbox />}
                          label="Override Test Series Export Configuration"
                          name="isActive"
                          checked={values.isActive}
                          onChange={handleChange}
                        />
                      </FormGroup>
                    </InfoBox>
                  )}
                  <InfoBox text={getInfoText("rows")}>
                    <CustomDropdownMultiple
                      chips
                      name="rows"
                      labelText="Rows"
                      options={availableRowOptions}
                      value={values.rows}
                      onChange={handleChange}
                    />
                  </InfoBox>
                  <InfoBox text={getInfoText("columns")}>
                    <CustomDropdownMultiple
                      chips
                      name="columns"
                      labelText="Columns"
                      options={availableColumnOptions}
                      value={values.columns}
                      onChange={handleChange}
                    />
                  </InfoBox>
                  {formFieldKeys
                    .filter((option) => !["rows", "columns", "options", "raw", "isActive"].includes(option))
                    .map((option) => {
                      const options =
                        (initialValues[option as keyof typeof initialValues] as unknown as string[]) || [];
                      const value = (values[option] as unknown as string[]) || [];
                      const labelText = matrixConfigurationOptionsMap.get(option) as unknown as string;
                      return (
                        <InfoBox text={getInfoText(option)}>
                          <CustomDropdownMultiple
                            chips
                            name={option}
                            labelText={labelText}
                            options={options}
                            value={value}
                            onChange={handleChange}
                          />
                        </InfoBox>
                      );
                    })}
                </Grid>
                <Grid container item direction="row" justify="flex-end">
                  <Box marginLeft={3}>
                    <CustomButton disabled={isSubmitting || !dirty} text="Revert" type="reset" />
                  </Box>
                  <Box marginLeft={3}>
                    <CustomButton primary disabled={isSubmitting} text="Save" type="submit" />
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
}

export default MatrixExportConfigurationDialog;
